// src/App.jsx
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import Home from './features/home/Home';
import PropertyDetail from './features/properties/PropertyDetail';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import LegalTermsModal, { LEGAL_TERMS_STORAGE_KEY } from './features/legal/LegalTermsModal';

// Lazy-loaded routes (code splitting)
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'));
const CompleteRegistration = lazy(() => import('./features/auth/CompleteRegistration'));
const PublishProperty = lazy(() => import('./features/dashboard/PublishProperty'));
const MyProperties = lazy(() => import('./features/dashboard/MyProperties'));
const EditProfile = lazy(() => import('./features/dashboard/EditProfile'));
const Settings = lazy(() => import('./features/dashboard/Settings'));
const ContractsList = lazy(() => import('./features/contracts/ContractsList'));
const CreateContract = lazy(() => import('./features/contracts/CreateContract'));
const ContractView = lazy(() => import('./features/contracts/ContractView'));
const ContractTemplateEditor = lazy(() => import('./features/contracts/ContractTemplateEditor'));
const QuickSign = lazy(() => import('./features/contracts/QuickSign'));
const InventoryManager = lazy(() => import('./features/contracts/InventoryManager'));
const AdminLogin = lazy(() => import('./features/admin/AdminLogin'));
const SuperAdminDashboard = lazy(() => import('./features/admin/SuperAdminDashboard'));
const MaintenancePage = lazy(() => import('./features/maintenance/MaintenancePage'));
const MaintenanceOwnerView = lazy(() => import('./features/maintenance/MaintenanceOwnerView'));
const StudentVerification = lazy(() => import('./features/students/StudentVerification'));
const AdminVerificationPanel = lazy(() => import('./features/students/AdminVerificationPanel'));
const StudentsPage = lazy(() => import('./features/students/StudentsPage'));
const AboutPage = lazy(() => import('./features/company/AboutPage'));
const ContactPage = lazy(() => import('./features/company/ContactPage'));
const BlogPage = lazy(() => import('./features/blog/BlogPage'));
const TermsPage = lazy(() => import('./features/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./features/legal/PrivacyPage'));
const Messages = lazy(() => import('./features/chat/Messages'));
const PaymentPage = lazy(() => import('./features/payments/PaymentPage'));
const PaymentResultPage = lazy(() => import('./features/payments/PaymentResultPage'));
const PaymentsDashboard = lazy(() => import('./features/payments/PaymentsDashboard'));
const ComparePage = lazy(() => import('./features/properties/ComparePage'));
const CompareBar = lazy(() => import('./components/compare/CompareBar'));
const AIAssistant = lazy(() => import('./components/ai/AIAssistant'));
const ProvidersAdmin = lazy(() => import('./features/admin/ProvidersAdmin'));
const EarningsDashboard = lazy(() => import('./features/admin/EarningsDashboard'));
const PricingPage = lazy(() => import('./features/plans/PricingPage'));
const NotFound = lazy(() => import('./features/NotFound'));
const AgencyPage = lazy(() => import('./features/agency/AgencyPage'));
const TourCreator = lazy(() => import('./features/tours/TourCreator'));

const Loading = () => (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="w-8 h-8 border-3 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
);

function App() {
    const [showLegalModal, setShowLegalModal] = useState(false);

    useEffect(() => {
        const accepted = localStorage.getItem(LEGAL_TERMS_STORAGE_KEY) === 'accepted';
        if (!accepted) {
            setShowLegalModal(true);
        }
    }, []);

    return (
        <>
            <ToastContainer position="bottom-right" theme="colored" autoClose={3000} />
            <LegalTermsModal
                isOpen={showLegalModal}
                onClose={() => setShowLegalModal(false)}
                persistOnAccept={true}
                showCloseButton={false}
                title="Terminos y Condiciones de Uso de MyHoommy"
            />
            <Suspense fallback={<Loading />}>
                <Routes>
                    {/* Admin Routes */}
                    <Route path="/hoommy_admin" element={<AdminLogin />} />
                    <Route
                        path="/hoommy_admin/dashboard"
                        element={
                            <AdminRoute>
                                <SuperAdminDashboard />
                            </AdminRoute>
                        }
                    />

                    <Route path="/" element={<Home />} />
                    <Route path="/estudiantes" element={<StudentsPage />} />
                    <Route path="/nosotros" element={<AboutPage />} />
                    <Route path="/contacto" element={<ContactPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/terminos" element={<TermsPage />} />
                    <Route path="/privacidad" element={<PrivacyPage />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/publish"
                        element={
                            <ProtectedRoute>
                                <PublishProperty />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-properties"
                        element={
                            <ProtectedRoute>
                                <MyProperties />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <EditProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute>
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contracts"
                        element={
                            <ProtectedRoute>
                                <ContractsList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contracts/new"
                        element={
                            <ProtectedRoute>
                                <CreateContract />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contracts/:id"
                        element={
                            <ProtectedRoute>
                                <ContractView />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/inventory/:contractId"
                        element={
                            <ProtectedRoute>
                                <InventoryManager />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/templates"
                        element={
                            <ProtectedRoute>
                                <ContractTemplateEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/maintenance/:contractId"
                        element={
                            <ProtectedRoute>
                                <MaintenancePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/maintenance/owner"
                        element={
                            <ProtectedRoute>
                                <MaintenanceOwnerView />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/verificar-estudiante"
                        element={
                            <ProtectedRoute>
                                <StudentVerification />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/verificaciones"
                        element={
                            <AdminRoute>
                                <AdminVerificationPanel />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/messages"
                        element={
                            <ProtectedRoute>
                                <Messages />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/payments-dashboard"
                        element={
                            <ProtectedRoute>
                                <PaymentsDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/providers"
                        element={
                            <AdminRoute>
                                <ProvidersAdmin />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/earnings"
                        element={
                            <AdminRoute>
                                <EarningsDashboard />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/payments/:contractId"
                        element={
                            <ProtectedRoute>
                                <PaymentPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/payment-result"
                        element={
                            <ProtectedRoute>
                                <PaymentResultPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/complete-registration/:personaId"
                        element={
                            <ProtectedRoute>
                                <CompleteRegistration />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/planes" element={<PricingPage />} />
                    <Route path="/agencia/:slug" element={<AgencyPage />} />
                    <Route path="/property/:id" element={<PropertyDetail />} />
                    <Route
                        path="/property/:id/create-tour"
                        element={
                            <ProtectedRoute>
                                <TourCreator />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/compare" element={<ComparePage />} />
                    <Route path="/firmar/:token" element={<QuickSign />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <CompareBar />
                <AIAssistant />
            </Suspense>
        </>
    );
}

export default App;
